import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { CURATOR_TV_SITE_NAME, getApplePath, getApplePersonPath } from "./components/appleTvLinks";
import { appleAccountPath, appleHomePath, appleSearchPath, toAppleRelativePath } from "./appleTvHost";
import { APPLE_API_ORIGIN, REQUEST_HEADERS } from "./appleTvApi";
import { bindAppleTopBarScrollState } from "./appleScroll";
import AppleTvMobileMenuButton from "./AppleTvMobileMenuButton";
import useCurrentCuratorProfilePath from "./useCurrentCuratorProfilePath";

const IMAGE_BASE = "https://image.tmdb.org/t/p/w185";
const SEARCH_QUERY_HISTORY_STORAGE_KEY = "curator:appleSearchQueryHistory";
const SEARCH_DEBOUNCE_MS = 240;
const MAX_RESULTS = 8;
const MAX_RECENT = 8;

function getUserDisplayName(user) {
  const first = String(user?.given_name || "").trim();
  const last = String(user?.family_name || "").trim();
  if (first || last) return `${first} ${last}`.trim();
  const full = String(user?.name || "").trim();
  if (full) return full;
  const nick = String(user?.nickname || "").trim();
  if (nick) return nick;
  const email = String(user?.email || "").trim();
  if (email) return email.split("@")[0];
  return "Account";
}

function getInitials(value) {
  const cleaned = String(value || "").trim();
  if (!cleaned) return "A";
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return `${parts[0].slice(0, 1)}${parts[1].slice(0, 1)}`.toUpperCase();
}

function readRecentQueries() {
  try {
    const raw = window.localStorage.getItem(SEARCH_QUERY_HISTORY_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return (Array.isArray(parsed) ? parsed : [])
      .map((entry) => String(entry || "").trim())
      .filter(Boolean)
      .slice(0, MAX_RECENT);
  } catch (_err) {
    return [];
  }
}

function rememberQuery(term) {
  const clean = String(term || "").trim();
  if (!clean) return;
  const current = readRecentQueries();
  const next = [clean, ...current.filter((q) => q.toLowerCase() !== clean.toLowerCase())].slice(0, MAX_RECENT);
  try {
    window.localStorage.setItem(SEARCH_QUERY_HISTORY_STORAGE_KEY, JSON.stringify(next));
  } catch (_err) {
    // no-op
  }
}

function toResult(row, mediaType) {
  const tmdbId = Number(row?.id);
  if (!Number.isFinite(tmdbId) || tmdbId <= 0) return null;
  const title = row?.title || row?.name || "Untitled";
  const year = String(row?.release_date || row?.first_air_date || "").slice(0, 4) || "";
  return {
    kind: mediaType === "tv-show" ? "tv-show" : "movie",
    tmdbId,
    mediaType,
    title,
    poster: row?.poster_path ? `${IMAGE_BASE}${row.poster_path}` : "",
    subLabel: `${mediaType === "tv-show" ? "TV" : "Movie"}${year ? ` · ${year}` : ""}`,
  };
}

function toPersonResult(row) {
  const tmdbId = Number(row?.id);
  if (!Number.isFinite(tmdbId) || tmdbId <= 0) return null;
  const dept = row?.known_for_department;
  return {
    kind: "person",
    tmdbId,
    mediaType: "person",
    title: row?.name || "Unknown",
    poster: row?.profile_path ? `${IMAGE_BASE}${row.profile_path}` : "",
    subLabel: dept === "Acting" ? "Actor" : (dept || "Person"),
  };
}

function ChromeIcon({ type }) {
  const stroke = 1.45;
  if (type === "home") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
          d="M12 5.1 5.2 10.6V19h13.6v-8.4L12 5.1z"
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinejoin="round"
        />
        <path d="M10.4 19v-4.6h3.2V19" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinejoin="round" />
      </svg>
    );
  }
  if (type === "back") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
          d="M14.5 5.5 8.5 12l6 6.5"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (type === "search") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <circle cx="10.5" cy="10.5" r="5.5" fill="none" stroke="currentColor" strokeWidth={stroke} />
        <path d="M14.8 14.8 18.8 18.8" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" />
      </svg>
    );
  }
  if (type === "clear") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
          d="M7.8 7.8l8.4 8.4M16.2 7.8l-8.4 8.4"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.6}
          strokeLinecap="round"
        />
      </svg>
    );
  }
  if (type === "clock") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <circle cx="12" cy="12" r="6.8" fill="none" stroke="currentColor" strokeWidth={stroke} />
        <path d="M12 8.6v3.5l2.4 1.6" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return null;
}

const AppleTvMobileTopBar = ({ title = CURATOR_TV_SITE_NAME }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loginWithRedirect, user } = useAuth0();
  // Resolve + cache curator username so appleAccountPath() can return /{user}/films.
  useCurrentCuratorProfilePath({
    isAuthenticated,
    user,
    fallbackPath: "/apple/account",
  });
  const accountPath = appleAccountPath();
  const showBackButton = useMemo(() => {
    const relative = toAppleRelativePath(location.pathname) || location.pathname || "";
    return relative === "/list" || relative.startsWith("/list/");
  }, [location.pathname]);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth <= 740 : false));
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [recent, setRecent] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropdownAnchor, setDropdownAnchor] = useState(null);
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);
  const pushedSearchHistoryRef = useRef(false);
  const resultNavLockRef = useRef(0);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => {
      const mobile = window.innerWidth <= 740;
      setIsMobile(mobile);
      if (!mobile) setSearchExpanded(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => bindAppleTopBarScrollState(setScrolled), []);

  useEffect(() => {
    setAvatarLoadFailed(false);
  }, [user?.picture]);

  const collapseExpandedSearch = useCallback((fromPopState = false) => {
    setSearchExpanded(false);
    setOpen(false);
    setResults([]);
    if (typeof document !== "undefined" && document.activeElement === inputRef.current) {
      inputRef.current?.blur();
    }
    if (!fromPopState && pushedSearchHistoryRef.current && typeof window !== "undefined") {
      pushedSearchHistoryRef.current = false;
      if (window.history.state?.appleMobileChromeSearch) {
        window.history.back();
      }
    } else {
      pushedSearchHistoryRef.current = false;
    }
  }, []);

  const expandSearch = useCallback(() => {
    // Mount the field + focus in the same tap so iOS/Android open the keyboard.
    flushSync(() => {
      setSearchExpanded(true);
      setRecent(readRecentQueries());
      setOpen(true);
    });
    if (typeof window !== "undefined" && !window.history.state?.appleMobileChromeSearch) {
      window.history.pushState({ appleMobileChromeSearch: true }, "");
      pushedSearchHistoryRef.current = true;
    }
    const input = inputRef.current;
    if (!input) return;
    input.focus({ preventScroll: true });
    try {
      const len = String(input.value || "").length;
      input.setSelectionRange(len, len);
    } catch (_err) {
      // Some input types reject selection ranges.
    }
  }, []);

  // Dropdown is portaled to <body> (outside the chrome header). Treat it as
  // inside search so result taps are not collapsed via history.back().
  useEffect(() => {
    const isInSearchDropdown = (target) => {
      if (!target || typeof target !== "object") return false;
      if (dropdownRef.current?.contains(target)) return true;
      return Boolean(target.closest?.(".apple-clone__mobile-chrome-bar-dropdown"));
    };
    const onDocClick = (e) => {
      if (!searchExpanded) return;
      if (isInSearchDropdown(e.target)) return;
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        collapseExpandedSearch();
      }
    };
    document.addEventListener("pointerdown", onDocClick, true);
    return () => document.removeEventListener("pointerdown", onDocClick, true);
  }, [collapseExpandedSearch, searchExpanded]);

  useEffect(() => {
    if (!searchExpanded) return undefined;
    const onPopState = () => {
      pushedSearchHistoryRef.current = false;
      collapseExpandedSearch(true);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [searchExpanded, collapseExpandedSearch]);

  useEffect(() => {
    if (!searchExpanded) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") collapseExpandedSearch();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [searchExpanded, collapseExpandedSearch]);

  const runSearch = useCallback((term) => {
    const clean = String(term || "").trim();
    if (clean.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    const reqId = ++reqIdRef.current;
    setLoading(true);
    const enc = encodeURIComponent(clean);
    Promise.all([
      fetch(`${APPLE_API_ORIGIN}/api/3/search/movie?query=${enc}&page=1`, { headers: REQUEST_HEADERS }).then((r) => (r.ok ? r.json() : { results: [] })).catch(() => ({ results: [] })),
      fetch(`${APPLE_API_ORIGIN}/api/3/search/tv?query=${enc}&page=1`, { headers: REQUEST_HEADERS }).then((r) => (r.ok ? r.json() : { results: [] })).catch(() => ({ results: [] })),
      fetch(`${APPLE_API_ORIGIN}/api/3/search/person?query=${enc}&page=1`, { headers: REQUEST_HEADERS }).then((r) => (r.ok ? r.json() : { results: [] })).catch(() => ({ results: [] })),
    ]).then(([movieJson, tvJson, personJson]) => {
      if (reqId !== reqIdRef.current) return;
      const movies = (movieJson?.results || []).map((row) => toResult(row, "movie")).filter(Boolean);
      const tv = (tvJson?.results || []).map((row) => toResult(row, "tv-show")).filter(Boolean);
      const people = (personJson?.results || []).map(toPersonResult).filter(Boolean);
      const merged = [];
      for (let i = 0; i < Math.max(movies.length, tv.length, people.length); i += 1) {
        if (movies[i]) merged.push(movies[i]);
        if (tv[i]) merged.push(tv[i]);
        if (people[i]) merged.push(people[i]);
      }
      setResults(merged.slice(0, MAX_RESULTS));
      setLoading(false);
    });
  }, []);

  const onChange = (e) => {
    const next = e.target.value;
    setValue(next);
    setOpen(true);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => runSearch(next), SEARCH_DEBOUNCE_MS);
  };

  const dismissExpandedForNavigation = () => {
    setSearchExpanded(false);
    setOpen(false);
    pushedSearchHistoryRef.current = false;
    if (typeof window !== "undefined" && window.history.state?.appleMobileChromeSearch) {
      window.history.replaceState(null, "");
    }
  };

  const submitQuery = (term) => {
    const clean = String(term || "").trim();
    if (!clean) return;
    rememberQuery(clean);
    dismissExpandedForNavigation();
    navigate(`${appleSearchPath()}?q=${encodeURIComponent(clean)}`);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    submitQuery(value);
  };

  const pathForResult = (item) => (
    item?.kind === "person"
      ? getApplePersonPath({ id: item.tmdbId, name: item.title })
      : getApplePath(item)
  );

  const goToResult = (item, event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const path = pathForResult(item);
    if (!path) return;
    const now = Date.now();
    if (now - resultNavLockRef.current < 500) return;
    resultNavLockRef.current = now;
    rememberQuery(value);
    setValue("");
    dismissExpandedForNavigation();
    navigate(path);
  };

  const dropdownOpen = searchExpanded && open;
  const showRecent = dropdownOpen && value.trim().length < 2 && recent.length > 0;
  const showResults = dropdownOpen && value.trim().length >= 2;

  // Keep results panel aligned to the search field and fully inside the viewport.
  useLayoutEffect(() => {
    if (!isMobile || (!showRecent && !showResults)) {
      setDropdownAnchor(null);
      return undefined;
    }
    const sync = () => {
      const form = searchRef.current;
      if (!form) return;
      const field = form.querySelector(".apple-clone__mobile-chrome-bar-search-field") || form;
      const rect = field.getBoundingClientRect();
      const margin = 12;
      const vw = window.innerWidth || document.documentElement.clientWidth || 0;
      let left = Math.round(rect.left);
      let width = Math.round(rect.width);
      if (left < margin) {
        width -= margin - left;
        left = margin;
      }
      const maxRight = vw - margin;
      if (left + width > maxRight) {
        width = maxRight - left;
      }
      width = Math.max(160, width);
      setDropdownAnchor({
        top: Math.round(rect.bottom + 8),
        left,
        width,
      });
    };
    sync();
    window.addEventListener("resize", sync);
    window.addEventListener("scroll", sync, true);
    return () => {
      window.removeEventListener("resize", sync);
      window.removeEventListener("scroll", sync, true);
    };
  }, [isMobile, showRecent, showResults, results, loading, recent, value, searchExpanded]);

  if (!isMobile) return null;

  const profileDisplayName = getUserDisplayName(user);
  const profileInitials = getInitials(profileDisplayName);
  const profilePicture = String(user?.picture || "").trim();
  const hasProfileAvatar = Boolean(profilePicture) && !avatarLoadFailed;
  const profileControl = isAuthenticated ? (
    <Link
      to={accountPath}
      className="apple-clone__mobile-chrome-bar-profile"
      aria-label="Account"
      title="Account"
    >
      {hasProfileAvatar ? (
        <img src={profilePicture} alt="" onError={() => setAvatarLoadFailed(true)} />
      ) : (
        <span className="apple-clone__mobile-chrome-bar-profile-fallback" aria-hidden="true">{profileInitials}</span>
      )}
    </Link>
  ) : (
    <button
      type="button"
      className="apple-clone__mobile-chrome-bar-profile apple-clone__mobile-chrome-bar-profile--signin"
      onClick={() => loginWithRedirect()}
      aria-label="Account"
      title="Account"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <circle cx="12" cy="8.6" r="3.3" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <path d="M5.8 18.8c1.2-2.9 3.4-4.3 6.2-4.3s5 1.4 6.2 4.3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    </button>
  );

  const bar = (
    <header
      ref={rootRef}
      className={`apple-clone__mobile-chrome-bar${searchExpanded ? " is-search-expanded" : ""}${scrolled ? " is-scrolled" : " is-at-top"}`}
      role="banner"
    >
      {searchExpanded ? (
        <>
          <div className="apple-clone__mobile-chrome-bar-slot apple-clone__mobile-chrome-bar-slot--left">
            <button
              type="button"
              className="apple-clone__mobile-menu-btn apple-clone__mobile-chrome-bar-btn--back"
              onClick={() => collapseExpandedSearch()}
              aria-label="Close search"
              title="Back"
            >
              <ChromeIcon type="back" />
            </button>
          </div>
          <div className="apple-clone__mobile-chrome-bar-search">
            <form
              className="apple-clone__mobile-chrome-bar-search-inner"
              onSubmit={onSubmit}
              role="search"
              ref={searchRef}
            >
              <div className="apple-clone__mobile-chrome-bar-search-field">
                <span className="apple-clone__mobile-chrome-bar-search-icon" aria-hidden="true">
                  <ChromeIcon type="search" />
                </span>
                <input
                  ref={inputRef}
                  type="search"
                  inputMode="search"
                  enterKeyHint="search"
                  autoCapitalize="off"
                  autoCorrect="off"
                  autoComplete="off"
                  spellCheck={false}
                  className="apple-clone__mobile-chrome-bar-search-input"
                  placeholder="Search movies, shows, people"
                  value={value}
                  onChange={onChange}
                  onFocus={() => {
                    setRecent(readRecentQueries());
                    setOpen(true);
                  }}
                  aria-label="Search"
                />
                {value ? (
                  <button
                    type="button"
                    className="apple-clone__mobile-chrome-bar-search-clear"
                    aria-label="Clear search"
                    onClick={() => {
                      setValue("");
                      setResults([]);
                      inputRef.current?.focus();
                    }}
                  >
                    <ChromeIcon type="clear" />
                  </button>
                ) : null}
              </div>
            </form>
          </div>
        </>
      ) : (
        <>
          <div className="apple-clone__mobile-chrome-bar-slot apple-clone__mobile-chrome-bar-slot--left">
            <div className="apple-clone__mobile-chrome-bar-btn-group" role="group" aria-label="Navigation">
              {showBackButton ? (
                <>
                  <button
                    type="button"
                    className="apple-clone__mobile-menu-btn apple-clone__mobile-chrome-bar-btn--back"
                    onClick={() => {
                      if (typeof window !== "undefined" && window.history.length > 1) {
                        navigate(-1);
                        return;
                      }
                      navigate(appleHomePath());
                    }}
                    aria-label="Go back"
                    title="Back"
                  >
                    <ChromeIcon type="back" />
                  </button>
                  <span className="apple-clone__mobile-chrome-bar-btn-divider" aria-hidden="true" />
                </>
              ) : null}
              <AppleTvMobileMenuButton />
              <span className="apple-clone__mobile-chrome-bar-btn-divider" aria-hidden="true" />
              <Link
                to={appleHomePath()}
                className="apple-clone__mobile-menu-btn apple-clone__mobile-chrome-bar-btn--home"
                aria-label="Home"
                title="Home"
              >
                <ChromeIcon type="home" />
              </Link>
            </div>
          </div>
          <div className="apple-clone__mobile-chrome-bar-title" aria-hidden="false">
            <Link to={appleHomePath()} className="apple-clone__mobile-chrome-bar-title-link" aria-label="Go to home">
              <span className="apple-clone__mobile-chrome-bar-title-text">{title}</span>
            </Link>
          </div>
          <div className="apple-clone__mobile-chrome-bar-slot apple-clone__mobile-chrome-bar-slot--right">
            <button
              type="button"
              className="apple-clone__mobile-menu-btn apple-clone__mobile-chrome-bar-btn--search"
              onClick={expandSearch}
              aria-label="Search"
              title="Search"
            >
              <ChromeIcon type="search" />
            </button>
            {profileControl}
          </div>
        </>
      )}
    </header>
  );

  const dropdownPanel = dropdownAnchor && (showRecent || showResults) ? (
    <div
      ref={dropdownRef}
      className="apple-clone__mobile-chrome-bar-dropdown"
      style={{
        top: dropdownAnchor.top,
        left: dropdownAnchor.left,
        width: dropdownAnchor.width,
        maxWidth: "calc(100vw - 24px)",
        right: "auto",
      }}
    >
      {showRecent
        ? recent.map((term) => (
          <button
            type="button"
            key={term}
            className="apple-clone__mobile-chrome-bar-dropdown-row apple-clone__mobile-chrome-bar-dropdown-row--recent"
            onPointerDown={(event) => {
              if (event.button != null && event.button !== 0) return;
              event.preventDefault();
              event.stopPropagation();
              submitQuery(term);
            }}
            onClick={(event) => {
              event.preventDefault();
              submitQuery(term);
            }}
          >
            <span className="apple-clone__mobile-chrome-bar-dropdown-icon" aria-hidden="true">
              <ChromeIcon type="clock" />
            </span>
            <span className="apple-clone__mobile-chrome-bar-dropdown-text">{term}</span>
          </button>
        ))
        : null}
      {showResults ? (
        <>
          {loading && !results.length ? (
            <div className="apple-clone__mobile-chrome-bar-dropdown-row apple-clone__mobile-chrome-bar-dropdown-row--status">
              Searching…
            </div>
          ) : null}
          {results.map((item) => (
            <Link
              key={`${item.kind}-${item.tmdbId}`}
              to={pathForResult(item)}
              className="apple-clone__mobile-chrome-bar-dropdown-row"
              onPointerDown={(event) => {
                if (event.button != null && event.button !== 0) return;
                goToResult(item, event);
              }}
              onClick={(event) => {
                goToResult(item, event);
              }}
            >
              <span className={`apple-clone__mobile-chrome-bar-thumb${item.kind === "person" ? " apple-clone__mobile-chrome-bar-thumb--person" : ""}`}>
                {item.poster ? <img src={item.poster} alt="" loading="lazy" /> : null}
              </span>
              <span className="apple-clone__mobile-chrome-bar-dropdown-body">
                <span className="apple-clone__mobile-chrome-bar-dropdown-text">{item.title}</span>
                <span className="apple-clone__mobile-chrome-bar-dropdown-sub">{item.subLabel}</span>
              </span>
            </Link>
          ))}
          {!loading && !results.length ? (
            <div className="apple-clone__mobile-chrome-bar-dropdown-row apple-clone__mobile-chrome-bar-dropdown-row--status">
              No matches
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  ) : null;

  if (typeof document === "undefined") return bar;

  return createPortal(
    <>
      {bar}
      {dropdownPanel}
    </>,
    document.body,
  );
};

export default AppleTvMobileTopBar;
